import { useState } from "react";
import { appConfirm } from "../../../components/ConfirmDialog";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { Send, Trash2 } from "lucide-react";
import { BACKEND, money } from "./shared";
import { RegenerateVisualsButton } from "../RegenerateVisualsButton";
import { EmailFlash } from "../../../components/EmailFlash";
import { DepositBalanceEmailPrompt } from "./DepositBalanceEmail";

const STATUSES = ["pending_payment", "site_visit_booked", "confirmed", "deposit_paid", "in_progress", "completed", "cancelled", "refunded"];

export function Overview({ b, canManage, reload, role, onClose }) {
  const [sendingLink, setSendingLink] = useState(false);
  const [photoBust, setPhotoBust] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const isAdminLevel = role === "admin" || role === "senior_admin";
  const quoteId = b.quote_id || (b.quote_photo_url && (b.quote_photo_url.match(/pd-render\/([^/?]+)/) || [])[1]) || "";
  const setStatus = async (status) => {
    try {
      const r = await api.put(`/admin/booking/${b.reference}/status`, { status });
      toast.success(r.data.moved_to_trash ? "Cancelled — job moved to Trash" : "Status updated");
      if (r.data.moved_to_trash && onClose) onClose();
      else reload();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };
  const moveToTrash = async () => {
    if (!(await appConfirm({ title: `Move job file ${b.reference} to Trash?`, description: "A senior admin can restore it.", confirmLabel: "Move to Trash", destructive: true }))) return;
    setDeleting(true);
    try {
      await api.post(`/admin/booking/${b.reference}/delete`);
      toast.success(`Job ${b.reference} moved to Trash`);
      if (onClose) onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
    setDeleting(false);
  };
  const sendPaymentLink = async () => {
    setSendingLink(true);
    try {
      const r = await api.post(`/admin/booking/${b.reference}/send-payment-link`);
      toast.success(r.data.sent ? `Payment link emailed to ${r.data.to}` : "Logged, but email could not be delivered — check Settings → Connected email.");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send payment link"); }
    setSendingLink(false);
  };
  const Row = ({ k, v }) => <div className="flex justify-between border-b border-[#E5E7EB] py-2 text-sm"><span className="text-[#6B7280]">{k}</span><span className="font-medium text-right">{v || "—"}</span></div>;
  return (
    <div data-testid="jobfile-overview">
      {(b.quote_reference || b.quote_photo_url) && (
        <div className="border border-[#E5E7EB] bg-[#F4F4F4] p-3 mb-4 flex items-start gap-3" data-testid="jobfile-quote-card">
          {b.quote_photo_url && (
            <img src={`${BACKEND}${b.quote_photo_url}${photoBust ? `?v=${photoBust}` : ""}`} alt="Quoted design"
              loading="lazy" onError={(e) => { e.target.style.display = "none"; }}
              className="w-28 h-20 object-cover border border-[#E5E7EB] bg-white flex-shrink-0"
              data-testid="jobfile-quote-photo" />
          )}
          <div className="text-sm flex-1">
            <div className="label-eyebrow mb-0.5">Quote on file</div>
            <div className="text-lg font-black text-[#C79A3B]" data-testid="jobfile-quote-reference">{b.quote_reference || "—"}</div>
            <div className="text-xs text-[#6B7280] mt-0.5">The design &amp; reference the client was quoted at booking.</div>
            {canManage && (b.status === "pending_payment" || b.status === "site_visit_booked") && (
              <button data-testid="jobfile-send-payment-link" onClick={sendPaymentLink} disabled={sendingLink}
                className="mt-2 inline-flex items-center gap-1.5 bg-[#C79A3B] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 hover:bg-[#A87E28] disabled:opacity-60 transition-colors">
                <Send size={13} /> {sendingLink ? "Sending…" : "Email payment link"}
              </button>
            )}
            {b.last_payment_link_sent_at && (
              <div className="text-[11px] text-[#6B7280] mt-1" data-testid="jobfile-last-link-sent">
                Last link sent {new Date(b.last_payment_link_sent_at).toLocaleString("en-GB")}
              </div>
            )}
            {canManage && quoteId && (
              <div className="mt-2">
                <RegenerateVisualsButton quoteId={quoteId} small testid="jobfile-regenerate-visuals"
                  onDone={() => setPhotoBust(Date.now())} />
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mb-4">
        <div className="label-eyebrow mb-1">Status</div>
        {canManage ? (
          <select data-testid="jobfile-status" value={b.status} onChange={e => setStatus(e.target.value)} className="border border-[#E5E7EB] px-3 py-2 text-sm bg-white">
            {STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
        ) : <div className="font-bold">{b.status?.replace(/_/g, " ")}</div>}
      </div>
      <ClientInfoCard b={b} canManage={canManage} reload={reload} />
      <Row k="Route" v={b.route === "planning" ? "Planning permission" : "Permitted Development"} />
      <StartDateEditor b={b} canManage={canManage} reload={reload} />
      <Row k="Express" v={b.express ? "Yes" : "No"} />
      <Row k="Deposit due" v={money(b.deposit_total)} />
      <Row k="Created" v={b.created_at ? new Date(b.created_at).toLocaleString("en-GB") : ""} />
      {b.comments && <div className="mt-3 text-sm"><div className="label-eyebrow mb-1">Client comments</div><p className="text-[#6B7280]">{b.comments}</p></div>}
      {(b.pricing_breakdown || []).length > 0 && (
        <div className="mt-4"><div className="label-eyebrow mb-1">Scope</div>
          <ul className="text-xs text-[#6B7280] list-disc pl-4">{(b.pricing_breakdown || []).map((x, i) => <li key={typeof x === "string" ? `${x}-${i}` : (x.label || i)}>{typeof x === "string" ? x : x.label}</li>)}</ul>
        </div>
      )}

      {isAdminLevel && !b.deleted && (
        <div className="mt-6 pt-4 border-t border-[#E5E7EB]">
          <div className="label-eyebrow mb-2 text-[#C0392B]">Danger zone</div>
          <button data-testid="jobfile-delete" onClick={moveToTrash} disabled={deleting}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#C0392B] border border-[#F1D4CE] px-3 py-2 hover:bg-[#C0392B] hover:text-white transition-colors disabled:opacity-60">
            <Trash2 size={14} /> {deleting ? "Moving…" : "Move job file to Trash"}
          </button>
          <p className="text-[11px] text-[#6B7280] mt-1.5">Soft-deleted — a senior admin can restore it. Cancelling a job (status → Cancelled) also moves it to Trash automatically.</p>
        </div>
      )}
    </div>
  );
}

function ClientInfoCard({ b, canManage, reload }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ name: b.name || "", email: b.email || "", phone: b.phone || "", address: b.address || "", postcode: b.postcode || "" });
  const start = () => { setF({ name: b.name || "", email: b.email || "", phone: b.phone || "", address: b.address || "", postcode: b.postcode || "" }); setEditing(true); };
  const save = async () => {
    if (f.email && !f.email.includes("@")) { toast.error("Enter a valid email address"); return; }
    try {
      await api.put(`/admin/booking/${b.reference}/client-info`, f);
      toast.success("Client details updated");
      setEditing(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update client details"); }
  };
  const Field = ({ label, k, type = "text" }) => (
    <label className="block">
      <span className="text-[11px] text-[#6B7280] uppercase tracking-wider">{label}</span>
      <input data-testid={`client-info-${k}`} type={type} value={f[k]} onChange={e => setF(s => ({ ...s, [k]: e.target.value }))}
        className="w-full border border-[#E5E7EB] px-2 py-1.5 text-sm mt-0.5" />
    </label>
  );
  const Row = ({ kLabel, v }) => <div className="flex justify-between border-b border-[#E5E7EB] py-2 text-sm"><span className="text-[#6B7280]">{kLabel}</span><span className="font-medium text-right">{v || "—"}</span></div>;

  if (!canManage) {
    return (<><Row kLabel="Email" v={b.email} /><Row kLabel="Phone" v={b.phone} /><Row kLabel="Site address" v={b.address} /><Row kLabel="Postcode" v={b.postcode} /></>);
  }
  return (
    <div className="border border-[#E5E7EB] bg-white p-3 mb-4" data-testid="jobfile-client-info">
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow">Client details</div>
        {!editing && <button data-testid="client-info-edit" onClick={start} className="text-[#C79A3B] text-xs uppercase tracking-widest font-bold hover:underline">Edit</button>}
      </div>
      {!editing ? (
        <>
          <Row kLabel="Name" v={b.name} />
          <Row kLabel="Email" v={b.email} />
          <Row kLabel="Phone" v={b.phone} />
          <Row kLabel="Site address" v={b.address} />
          <Row kLabel="Postcode" v={b.postcode} />
        </>
      ) : (
        <div className="space-y-2">
          <Field label="Name" k="name" />
          <Field label="Email" k="email" type="email" />
          <Field label="Phone" k="phone" />
          <Field label="Site address" k="address" />
          <Field label="Postcode" k="postcode" />
          <div className="flex gap-2 pt-1">
            <button data-testid="client-info-save" onClick={save} className="bg-[#12263A] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5">Save</button>
            <button onClick={() => setEditing(false)} className="text-[#6B7280] text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StartDateEditor({ b, canManage, reload }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(b.start_date || "");
  const daysToStart = b.start_date ? Math.ceil((new Date(b.start_date) - new Date()) / 86400000) : null;
  const feeWindow = daysToStart !== null && daysToStart >= 0 && daysToStart <= 14;
  const save = async () => {
    if (!val) { toast.error("Pick a start date"); return; }
    try {
      const r = await api.put(`/admin/booking/${b.reference}/start-date`, { start_date: val });
      toast.success(r.data.fee_may_apply
        ? "Start date updated — within 2 weeks of commencement, a rescheduling fee may apply."
        : "Start date updated");
      setEditing(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update start date"); }
  };
  if (!canManage) return <div className="flex justify-between border-b border-[#E5E7EB] py-2 text-sm"><span className="text-[#6B7280]">Start date</span><span className="font-medium text-right">{b.start_date || "—"}</span></div>;
  return (
    <div className="border-b border-[#E5E7EB] py-2 text-sm" data-testid="jobfile-start-date">
      <div className="flex justify-between items-center">
        <span className="text-[#6B7280]">Start date</span>
        {!editing ? (
          <span className="font-medium flex items-center gap-2">{b.start_date || "—"}
            <button data-testid="jobfile-start-date-edit" onClick={() => { setVal(b.start_date || ""); setEditing(true); }} className="text-[#C79A3B] text-xs uppercase tracking-widest font-bold hover:underline">Edit</button>
          </span>
        ) : (
          <span className="flex items-center gap-2">
            <input data-testid="jobfile-start-date-input" type="date" value={val} onChange={e => setVal(e.target.value)} className="border border-[#E5E7EB] px-2 py-1 text-sm" />
            <button data-testid="jobfile-start-date-save" onClick={save} className="bg-[#12263A] text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5">Save</button>
            <button onClick={() => setEditing(false)} className="text-[#6B7280] text-xs">Cancel</button>
          </span>
        )}
      </div>
      {editing && feeWindow && (
        <p className="text-[11px] text-[#A87E28] mt-1.5" data-testid="jobfile-start-date-warning">
          ⚠ Commencement is in {daysToStart} day{daysToStart === 1 ? "" : "s"}. Per our T&amp;Cs, changing a start date within 2 weeks of commencement may incur a rescheduling fee.
        </p>
      )}
    </div>
  );
}

export function SiteVisitCard({ b, canManage, reload }) {
  const [date, setDate] = useState(b.site_visit_date || "");
  const [time, setTime] = useState(b.site_visit_time || "");
  const [notes, setNotes] = useState(b.site_visit_notes || "");
  const [busy, setBusy] = useState(false);
  const status = b.site_visit_status || "";
  const TONE = { scheduled: "bg-[#FCE3D6] text-[#A87E28]", confirmed: "bg-[#12263A] text-white", completed: "bg-[#1F6B3B] text-white" };
  const save = async (newStatus) => {
    if (!date && newStatus !== "completed") { toast.error("Pick a site visit date"); return; }
    setBusy(true);
    try {
      await api.put(`/admin/booking/${b.reference}/site-visit`, { date, time, notes, ...(newStatus ? { status: newStatus } : {}) });
      toast.success(newStatus === "completed" ? "Site visit marked as completed" : "Site visit saved");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save site visit"); }
    setBusy(false);
  };
  const emailClient = async () => {
    if (!date) { toast.error("Set a site visit date first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/booking/${b.reference}/site-visit/email`);
      toast.success(r.data.sent ? `Site visit confirmation emailed to ${r.data.to}` : "Saved, but the email could not be sent — check Settings → Connected email.");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not email the client"); }
    setBusy(false);
  };
  return (
    <div className="border border-[#E5E7EB] bg-white p-3 mb-4" data-testid="jobfile-site-visit">
      <div className="flex items-center justify-between mb-2">
        <div className="label-eyebrow">Site visit (pre-construction survey)</div>
        {status && <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full ${TONE[status] || "bg-[#F0EAE0] text-[#6B7280]"}`} data-testid="site-visit-status">{status}</span>}
      </div>
      {!canManage ? (
        <div className="text-sm text-[#6B7280]">
          {b.site_visit_date ? <>Booked for <b className="text-[#12263A]">{b.site_visit_date}{b.site_visit_time ? ` at ${b.site_visit_time}` : ""}</b></> : "Not yet scheduled."}
          {b.site_visit_notes && <p className="mt-1 whitespace-pre-wrap">{b.site_visit_notes}</p>}
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-2">
            <div className="flex-1">
              <label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Date</label>
              <input data-testid="site-visit-date" type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full border border-[#E5E7EB] px-2 py-1.5 text-sm" />
            </div>
            <div className="w-28">
              <label className="text-[11px] text-[#6B7280] uppercase tracking-wider">Time</label>
              <input data-testid="site-visit-time" type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full border border-[#E5E7EB] px-2 py-1.5 text-sm" />
            </div>
          </div>
          <textarea data-testid="site-visit-notes" rows={2} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Internal site-visit notes (access, findings, actions)…" className="w-full border border-[#E5E7EB] px-2 py-1.5 text-sm mb-2" />
          <div className="flex flex-wrap gap-2">
            <button data-testid="site-visit-save" disabled={busy} onClick={() => save("scheduled")} className="de-btn-secondary text-xs disabled:opacity-60">Save</button>
            <button data-testid="site-visit-email" disabled={busy} onClick={emailClient} className="de-btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-60"><Send size={13} /> Email client confirmation</button>
            <button data-testid="site-visit-complete" disabled={busy} onClick={() => save("completed")} className="text-xs font-bold uppercase tracking-widest px-3 py-1.5 border border-[#1F6B3B] text-[#1F6B3B] hover:bg-[#1F6B3B] hover:text-white transition-colors disabled:opacity-60">Mark completed</button>
          </div>
          <div className="mt-1.5"><EmailFlash text="'Email client confirmation' emails the client their visit details. 'Mark completed' does NOT email automatically — you'll get a prompt below to preview, edit and send the balance-due email." /></div>
          <DepositBalanceEmailPrompt b={b} reload={reload} />
          {b.site_visit_completed_at && <p className="text-[11px] text-[#6B7280] mt-1.5">Completed {new Date(b.site_visit_completed_at).toLocaleString("en-GB")} · add findings in the <b>Notes</b> &amp; <b>Photos</b> tabs.</p>}
          {b.site_visit_email_sent_at && !b.site_visit_completed_at && <p className="text-[11px] text-[#6B7280] mt-1.5">Confirmation emailed {new Date(b.site_visit_email_sent_at).toLocaleString("en-GB")}.</p>}
          {(b.site_visit_history || []).slice(-3).reverse().map((h, i) => (
            <p key={h.at} className="text-[11px] text-[#5B2A83] mt-1" data-testid={`site-visit-moved-${i}`}>
              Moved by {h.by === "client" ? "the client (email link)" : h.by} on {new Date(h.at).toLocaleString("en-GB")}: {h.from_date} {h.from_time} → <b>{h.to_date} {h.to_time}</b>
            </p>
          ))}
          {!b.site_visit_completed_at && (b.site_visit_reminder_sent_at
            ? <p className="text-[11px] text-[#1F6B3B] mt-1" data-testid="site-visit-reminder-sent">Evening-before reminder emailed {new Date(b.site_visit_reminder_sent_at).toLocaleString("en-GB")}.</p>
            : b.site_visit_reminder_error
              ? <p className="text-[11px] text-[#C0392B] mt-1" data-testid="site-visit-reminder-failed">Reminder email failed — {b.site_visit_reminder_error}. Retries at 8pm.</p>
              : b.site_visit_date && <p className="text-[11px] text-[#9CA3AF] mt-1" data-testid="site-visit-reminder-pending">A reminder email goes to the client automatically at 6pm the evening before.</p>)}
        </>
      )}
    </div>
  );
}
